import type { ButtonRect, InputConfig } from "./config";
import type { InputSample, InputListener } from "./types";
import { GatherDetector } from "./gather-detector";
import { RollDetector } from "./roll-detector";
import { ThrowDetector } from "./throw-detector";
import { ButtonInput } from "./button-input";

export type DetectorMode = "gather" | "roll" | "throw" | null;

export class InputManager {
  private gatherDetector: GatherDetector;
  private rollDetector: RollDetector;
  private throwDetector: ThrowDetector;
  private buttonInput: ButtonInput;
  private activeMode: DetectorMode = null;
  private mapGestureSample: (sample: InputSample) => InputSample;

  constructor(
    config: InputConfig,
    nowTick: () => number,
    mapGestureSample: (sample: InputSample) => InputSample = (sample) => sample,
  ) {
    this.gatherDetector = new GatherDetector(config, nowTick);
    this.rollDetector = new RollDetector(config, nowTick);
    this.throwDetector = new ThrowDetector(config, nowTick);
    this.buttonInput = new ButtonInput(config, nowTick);
    this.mapGestureSample = mapGestureSample;
  }

  setButtonRects(dodge: ButtonRect, block: ButtonRect): void {
    this.buttonInput.setRects(dodge, block);
  }

  setActive(mode: DetectorMode): void {
    this.activeMode = mode;
    this.gatherDetector.setActive(mode === "gather");
    this.rollDetector.setActive(mode === "roll");
    this.throwDetector.setActive(mode === "throw");
  }

  onInput(listener: InputListener): void {
    this.gatherDetector.onInput(listener);
    this.rollDetector.onInput(listener);
    this.throwDetector.onInput(listener);
    this.buttonInput.onInput(listener);
  }

  processSample(sample: InputSample): void {
    // Dodge / block exist only in round 3, where they take priority.
    const isButton =
      this.activeMode === "throw" && this.buttonInput.processSample(sample);

    if (isButton && sample.phase === "start") {
      // Mark this pointer for throw detector
      this.throwDetector.markButtonPointer(sample.pointerId);
    }

    // Don't process gestures if this is a button touch
    if (!isButton) {
      const gestureSample = this.mapGestureSample(sample);
      switch (this.activeMode) {
        case "gather":
          this.gatherDetector.processSample(gestureSample);
          break;
        case "roll":
          this.rollDetector.processSample(gestureSample);
          break;
        case "throw":
          this.throwDetector.processSample(gestureSample);
          break;
      }
    }
  }
}
